import dayjs, { type Dayjs } from 'dayjs'
import duration from 'dayjs/plugin/duration'
import relativeTime from 'dayjs/plugin/relativeTime'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import updateLocale from 'dayjs/plugin/updateLocale'
import utcFormat from 'dayjs/plugin/utc'
import weekday from 'dayjs/plugin/weekday'
import LocalizedFormat from 'dayjs/plugin/localizedFormat'
import timezone from 'dayjs/plugin/timezone'
import minMax from 'dayjs/plugin/minMax'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { allTimezones } from 'shared/constants/allTimezone'

// Plugins
dayjs.extend(duration)

const config = {
  thresholds: [
    { l: 's', r: 1 },
    { l: 'ss', r: 59, d: 'second' },
    { l: 'm', r: 1 },
    { l: 'mm', r: 59, d: 'minute' },
    { l: 'h', r: 1 },
    { l: 'hh', r: 23, d: 'hour' },
    { l: 'd', r: 1 },
    { l: 'dd', r: 29, d: 'day' },
    { l: 'M', r: 1 },
    { l: 'MM', r: 11, d: 'month' },
    { l: 'y', r: 1 },
    { l: 'yy', d: 'year' },
  ],
}

dayjs.extend(relativeTime, config)
dayjs.extend(advancedFormat)
dayjs.extend(updateLocale)
dayjs.extend(utcFormat)
dayjs.extend(weekday)
dayjs.extend(LocalizedFormat)
dayjs.extend(timezone)
dayjs.extend(minMax)
dayjs.extend(customParseFormat)

type ITimezoneItem = {
  group?: string
  value: string
  label: string
  offsetMinutes: number
}

function getDateWithTimezone(
  date?: string | number | Date | dayjs.Dayjs | null | undefined,
  timezone?: string
) {
  try {
    return dayjs.utc(date).tz(timezone)
  } catch (error) {
    console.warn(`Invalid timezone specified: ${timezone}.`)

    return dayjs.utc(date)
  }
}

export default function localDate(
  date?: string | number | Date | dayjs.Dayjs | null | undefined,
  timezone?: string
) {
  return getDateWithTimezone(dayjs.utc(date), timezone)
}

export function utcDate(date?: string | number | Date | dayjs.Dayjs | null | undefined) {
  return dayjs.utc(date)
}

export function dateValueOf(date?: string | number | Date | dayjs.Dayjs | null | undefined) {
  return dayjs.utc(date).local().valueOf()
}

const localeConversation: ILocale = {
  name: 'en-conversations',
  relativeTime: {
    future: 'in %s',
    past: '%ss',
    s: '%ds',
    ss: '%ds',
    m: '1min',
    mm: '%dmin',
    h: '1h',
    hh: '%dh',
    d: '1d',
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    dd: (number: number) => {
      const day = number
      const week = Math.floor(number / 7)

      return week ? `${week}w` : `${day}d`
    },
    M: '1mo',
    MM: '%dmo',
    y: '1y',
    yy: '%dy',
  },
}

const localeEnStrict: ILocale = {
  name: 'en-strict',
  relativeTime: {
    future: 'in %s',
    past: '%s ago',
    s: '%d seconds',
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    ss: '%d seconds',
    m: '%d minute',
    mm: '%d minutes',
    h: '%d hour',
    hh: '%d hours',
    d: '%d day',
    dd: '%d days',
    M: '%d month',
    MM: '%d months',
    y: '%d year',
    yy: '%d years',
  },
}

dayjs.updateLocale('en', {
  relativeTime: {
    future: 'in %s',
    past: '%s ago',
    s: '%d seconds',
    ss: '%d seconds',
    m: 'a minute',
    mm: '%d minutes',
    h: 'an hour',
    hh: '%d hours',
    d: 'a day',
    dd: '%d days',
    M: 'a month',
    MM: '%d months',
    y: 'a year',
    yy: '%d years',
  },
})

export function dateFromNowForConversations(
  date?: string | number | Date | dayjs.Dayjs | null | undefined
) {
  return dayjs.utc(date).locale(localeConversation).fromNow(true)
}

export function dateFromNow(date?: string | number | Date | dayjs.Dayjs | null | undefined) {
  return dayjs.utc(date).local().fromNow(true)
}

export function dateFromNowStrict(date?: string | number | Date | dayjs.Dayjs | null | undefined) {
  return dayjs.utc(date).locale(localeEnStrict).fromNow(true)
}

export function dateDiff(date?: string | number | Date | dayjs.Dayjs | null | undefined) {
  const dateNow = dayjs.utc().local()

  return dateNow.diff(date, 'seconds')
}

export function localTime(timezone?: string) {
  const dateNow = dayjs.utc().local()
  const dateWithTimezone = getDateWithTimezone(dayjs(dateNow), timezone)

  return dateWithTimezone.format(DayjsFormats.localTime)
}

export function offsetFromUTC(timezone?: string) {
  const dateNow = dayjs.utc().local()
  const dateWithTimezone = getDateWithTimezone(dayjs(dateNow), timezone)

  return dateWithTimezone.format(DayjsFormats.UTCOffset)
}

export function getMaxDate(dates: string[]): Dayjs | null {
  return dayjs.max(dates.map((date) => dayjs(date)))
}

export function getTimestamp(value: string | number | Date | dayjs.Dayjs): number {
  return dayjs.utc(value).unix()
}

export enum DayjsFormats {
  full = 'MMM D, YYYY h:mm A',
  full2 = 'dddd, MMM D, YYYY [@] h:mm A z',
  full3 = 'ddd, MMM Do, YYYY',
  full4 = 'ddd, D MMM, YYYY, h:mm A',
  full5 = 'D MMM, YYYY',
  full6 = 'MMM D, YYYY',
  full7 = 'MMM D h:mm A',
  fullWithAtAndYear = 'MMM D, YYYY [at] h:mm a.',
  fullDownload = 'MMM D, YYYY h-mm-s A',
  fullWithAt = 'MMM D [at] h:mm A',
  fullWithAtLong = 'dddd, MMM D, YYYY [at] h:mm a',
  fullWithAtDash = 'D MMM, YYYY [-] h:mm A',
  fullWithAtDash2 = 'MMM D, YYYY [-] h:mm A',
  fullWithAtDash3 = 'MMM D, YYYY [-] h:mm a',
  fullWithTimezone = 'HH:mm:ss z YYYY-MM-DD',
  date = 'MMM D',
  date2 = 'DD MMM YYYY',
  dateSort = 'YYYY-MM-DD',
  time = 'h:mm a',
  time2 = 'h:mm A',
  time3 = 'HH:mm:ss',
  time4 = 'HH:mm',
  localTime = 'LT',
  UTCOffset = 'Z',
  timezone = 'z',
  apiFormat = 'YYYY-MM-DDTHH:mm:ssZ[Z]',
  apiFormat2 = 'YYYY-MM-DD HH:mm:ss',
  apiFormat3 = 'YYYY-MM-DD HH:mm',
  slash = 'MM/DD/YYYY',
  slashWithAtDash = 'MM/DD/YYYY [-] h:mm a',
  timestampFilter = 'YYYY-MM-DD:HH:mm',
  timestampServerDate = 'YYYY-MM-DD',
  timestampServerTime = 'HH:mm',
  downloadFile = 'YYYY_MM_DD',
}

export const periodsMap = {
  daily: 'day',
  weekly: 'week',
  monthly: 'month',
}

export type IPeriod = keyof typeof periodsMap

export const days = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const

export type IDays = (typeof days)[number]

// monday -> Mon
export const longShortDayMap: Map<IDays, string> = new Map(
  days.map((day, index) => [day, dayjs().startOf('week').add(index, 'day').format('ddd')])
)

// monday -> Mo
export const longShort2DayMap = new Map(
  days.map((day, index) => [day, dayjs().startOf('week').add(index, 'day').format('dd')])
)

export function getNumberWithOrdinal(n: number) {
  const s = ['th', 'st', 'nd', 'rd'],
    v = n % 100
  return n + (s[(v - 20) % 10] || s[v] || s[0])
}

export const toLocalISOString = (value: Date | null) => {
  if (!value) return value

  const year = value.getFullYear()
  const month = value.getMonth() + 1
  const day = value.getDate()
  const hours = value.getHours()
  const minutes = value.getMinutes()
  const fullDate = `${year}-${month}-${day}T${hours}:${minutes}`
  const correctDate = dayjs(fullDate).format(DayjsFormats.timestampServerDate)
  const correctTime = dayjs(fullDate).format(DayjsFormats.timestampServerTime)

  return `${correctDate}T${correctTime}:00.000Z`
}

export const toLocalISOStringStartOfDay = (value: Date | null) => {
  if (!value) return value

  return dayjs(value).startOf('day').toDate()
}

export const toLocalISOStringEndOfDay = (value: Date | null) => {
  if (!value) return value

  return dayjs(value).endOf('day').toDate()
}

export const formatSecondsToLargestUnit = (seconds: number): string => {
  if (seconds === 0) return '0s'

  const days = Math.floor(seconds / 86400)
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const remainingSeconds = Math.floor(seconds % 60)

  if (seconds >= 86400) {
    return days === 1 ? '1 day' : `${days} days`
  }

  if (seconds >= 3600) {
    return `${hours}h`
  }

  const parts: string[] = []
  if (minutes > 0) parts.push(`${minutes}m`)
  if (remainingSeconds > 0 || parts.length === 0) parts.push(`${remainingSeconds}s`)

  return parts.join(' ')
}

export const getTimezonesList = () => {
  const arr: ITimezoneItem[] = []

  allTimezones.forEach((zone) => {
    if (/\//g.test(zone) && !/\/.*\//g.test(zone)) {
      try {
        const name = `UTC ${dayjs.tz(dayjs(), zone).format('Z')} ${/\/(.*)/g.exec(zone)?.[1]}`
        const group = /(.*)\//g.exec(zone)?.[1]

        if (group === 'Etc') {
          return
        }

        const dateWithTimezone = dayjs().tz(zone)
        const minutesOffset = dateWithTimezone.utcOffset()

        arr.push({
          group,
          value: zone,
          label: name,
          offsetMinutes: minutesOffset,
        })
      } catch (e) {
        console.error(e)
      }
    }
  })

  return arr.sort((a, b) => a.offsetMinutes - b.offsetMinutes)
}
